// Onboarding final step (both paths, docs/06 §2): explain why notifications
// matter (friends' calls only ring if allowed), then request permission via
// the platform seam (web Notification API, or the native POST_NOTIFICATIONS
// prompt inside the Android shell). Never throws, never blocks onboarding.
//
// On mount we check the CURRENT grant (non-prompting) so a device that
// already granted before — a restore, or re-running onboarding — never sees
// this ask again: an already-granted read auto-advances through the same
// onNext() the "Enable" success path uses. Anything else fails OPEN to
// showing the step — only a confirmed grant ever skips it.
//
// The non-prompting read comes from the same platform seam as the request
// (push/notificationPermission.ts): native uses the plain check-only
// MerkuryPush.getNotificationPermissionStatus() plugin method, web reads
// Notification.permission. Unknown/error reads as "not granted", so the step
// fails open to showing — only a confirmed grant ever skips it.
import { useEffect, useState, type ReactNode } from 'react';
import {
  callPushBlockReason,
  hasNotificationPermission,
  requestNotificationPermission,
} from '../../push/notificationPermission.js';
import { InstallHintBody } from '../../screens/ui/InstallHint.js';
import { useT } from '../../i18n/index.js';

export function Notifications({
  onNext,
  onBack,
}: {
  onNext: () => void;
  onBack?: () => void;
}): ReactNode {
  const t = useT();
  const [checking, setChecking] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [denied, setDenied] = useState(false);
  // On an iOS Safari TAB there is no Web Push at all — a permission prompt here
  // is doomed (web/jobs.md §4). Detect that once and show the "add to Home
  // Screen" steps INSTEAD of the Enable button, so a new user is told how to
  // actually receive calls before they can miss their first one. Any other
  // context (desktop, an installed iOS app) keeps the normal permission flow.
  const [installIos] = useState(() => callPushBlockReason() === 'ios-install');

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const granted = await hasNotificationPermission();
      if (cancelled) return;
      if (granted) onNext();
      else setChecking(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleEnable(): Promise<void> {
    setRequesting(true);
    let granted = false;
    try {
      granted = await requestNotificationPermission();
    } finally {
      setRequesting(false);
    }
    // A denial gets a gentle pointer instead of a silent advance — but it
    // never blocks onboarding: Continue stays one tap away.
    if (granted) onNext();
    else setDenied(true);
  }

  // Never flash the request UI before an already-granted device auto-advances.
  if (checking) return null;

  // iOS Safari tab: the permission prompt can't work — show the install steps
  // and a plain Continue (onboarding never blocks). Reuses the exact banner
  // copy so the onboarding hint and the later signed-in banner never diverge.
  if (installIos) {
    return (
      <main className="mx-auto flex min-h-full w-full max-w-app flex-col gap-6 p-6">
        <InstallHintBody reason="ios-install" />
        <div className="flex gap-3">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="min-h-touch flex-1 rounded-xl border border-edge bg-surface px-4 font-medium text-ink"
            >
              {t('common.back')}
            </button>
          )}
          <button
            type="button"
            onClick={onNext}
            className="min-h-touch flex-1 rounded-xl border border-edge bg-surface px-4 font-medium text-ink"
          >
            {t('common.continue')}
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="mx-auto flex min-h-full w-full max-w-app flex-col gap-6 p-6">
      <h1 className="text-2xl font-semibold text-ink">{t('onbNotifications.title')}</h1>
      <p className="text-sm text-muted">{t('onbNotifications.help')}</p>
      {denied && (
        <p role="status" className="text-sm text-warn">
          {t('onbNotifications.denied')}
        </p>
      )}
      <div className="flex flex-col gap-3">
        {!denied && (
          <button
            type="button"
            disabled={requesting}
            onClick={() => void handleEnable()}
            className="min-h-touch rounded-xl bg-accent px-4 font-medium text-ink disabled:opacity-40 active:bg-accent-press"
          >
            {t('onbNotifications.enable')}
          </button>
        )}
        <div className="flex gap-3">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="min-h-touch flex-1 rounded-xl border border-edge bg-surface px-4 font-medium text-ink"
            >
              {t('common.back')}
            </button>
          )}
          <button
            type="button"
            onClick={onNext}
            className="min-h-touch flex-1 rounded-xl border border-edge bg-surface px-4 font-medium text-ink"
          >
            {denied ? t('common.continue') : t('onbNotifications.skip')}
          </button>
        </div>
      </div>
    </main>
  );
}
